import { useCallback, useRef, useState } from "react";
import { api } from "../lib/api";
import useAdminLoad, { payloadShapeError } from "./useAdminLoad";
import { DEFAULT_PAGE_SIZE, pageQuery } from "../affiliate/contract";

/**
 * THE product's one reader of the paginated list envelope `{rows, total, page, pageSize}`.
 *
 * Born as `admin/affiliate/useAffiliateList` — the four lists the practitioner reads and
 * the four the operator reads are the same envelope, the same clamp and the same failure
 * modes, so they were one hook rather than eight copies. The reviews module reads the very
 * same envelope on three planes (public profile, tenant admin, super admin), which is why
 * the hook lives here now: module-agnostic and plane-agnostic. `pageQuery` and
 * `DEFAULT_PAGE_SIZE` stay owned by `affiliate/contract.js` and are re-exported by every
 * later module's contract, so there is still exactly one clamp in the SPA.
 *
 * A FAILED READ IS NOT AN EMPTY LIST (BUG-051). `useAdminLoad` holds the failure as a
 * first-class state and the caller renders `LoadFailure` instead of "no commissions yet" —
 * telling an affiliate they have earned nothing when the read simply failed is a false
 * claim about their own money.
 *
 * THE PAGE RESETS WHEN THE FILTERS CHANGE, during render rather than in an effect: page 3
 * of an unfiltered list is not page 3 of a filtered one, and doing it in an effect would
 * fetch the old page first and then the right one — two requests and a visible flash. A
 * state adjustment during render is React's documented shape for derived state; the render
 * it interrupts is discarded before it commits.
 *
 * `params` may be a fresh object every render: it is compared by VALUE (its serialisation),
 * so a caller does not have to memoise it and cannot accidentally create a refetch loop —
 * `useAdminLoad` re-runs whenever its `load` identity changes.
 *
 * crm-module BEGIN C1.05 — `source` may also be a FETCHER, `(query) => Promise<envelope>`:
 * a module whose transport lives in one api file (admin/crm/api/crmApi.js is the only CRM
 * module allowed to import lib/api) hands its list function in instead of a path. The
 * fetcher must be STABLE (a module-level function or a `useCallback`), for the same reason
 * `load` must be stable in `useAdminLoad`: its identity is a dependency of the read.
 * crm-module END
 *
 * @param {string|((query: object) => Promise<object>)} path a list path from the calling
 *   module's contract (`AFFILIATE_PATHS`, `REVIEW_PATHS`), or a stable fetcher
 * @param {object} [params] filters, already narrowed to the ones the caller wants sent
 * @param {number} [pageSize]
 */
export default function usePagedList(path, params = {}, pageSize = DEFAULT_PAGE_SIZE) {
  const key = JSON.stringify(params);
  const [page, setPage] = useState(1);
  const keyRef = useRef(key);
  if (keyRef.current !== key) {
    keyRef.current = key;
    if (page !== 1) setPage(1);
  }

  const [body, setBody] = useState(null);
  const load = useCallback(async () => {
    const query = { ...JSON.parse(key), ...pageQuery(page, pageSize) };
    /* crm-module BEGIN C1.05 */
    const data = typeof path === "function"
      ? await path(query)
      : (await api.get(path, { params: query })).data;
    /* crm-module END */
    // A 200 that is not the envelope is not an empty list: the production proxy answers
    // 200 + index.html for an unknown /api path, and reading that as "nothing here" is the
    // same lie as swallowing a 500.
    if (!data || typeof data !== "object" || !Array.isArray(data.rows)
        || typeof data.total !== "number") {
      throw payloadShapeError();
    }
    setBody(data);
  }, [path, key, page, pageSize]);

  const { loading, loadError, reload } = useAdminLoad(load);

  return {
    rows: body?.rows || [],
    total: body?.total || 0,
    // The page the SERVER applied, not the one that was asked for: the clamp lives in the
    // repository and the response states what it actually used.
    page: body?.page || page,
    pageSize: body?.pageSize || pageSize,
    loaded: body !== null,
    /* crm-module BEGIN C1.06 — the whole answer, for lists whose envelope carries more than
       the rows (the client Sessions tab's `pack`). crm-module END */
    envelope: body,
    loading,
    loadError,
    reload,
    setPage,
  };
}
